import BaseSelect from 'components/Selects/BaseSelect/BaseSelect';
import { findSelectOption } from 'components/Selects/BaseSelect/utils/baseSelect.utils';
import { cloneElement, isValidElement, useEffect, useRef, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { BsChevronDown, BsSearch } from 'react-icons/bs';
import { FaXmark } from 'react-icons/fa6';

// Matches the compact pill the native select used inside this widget.
const DROPDOWN_SELECT_STYLES = {
  control: (base) => ({ ...base, minHeight: 28, fontSize: 13 }),
  valueContainer: (base) => ({ ...base, paddingInline: 10 }),
};

const CUSTOM_DROPDOWN_CONTENT_DELAY_MS = 240;
const CUSTOM_DROPDOWN_ANIMATION_MS = 320;

export default function BaseSearchDropdown({
  searchValue = '',
  onSearchValueChange = () => {},
  onSearch = () => {},
  searchPlaceholder = 'Search',
  searchAriaLabel = 'Search',
  selectedValue = '',
  onSelect = () => {},
  selectAriaLabel = 'Select option',
  options = [],
  defaultOptionLabel = 'All',
  dropdownContent = null,
  searchDropdownContent = null,
  dropdownContainerClassName = '',
  className = '',
  style,
}) {
  const [activeControl, setActiveControl] = useState('dropdown');
  const [customDropdownReady, setCustomDropdownReady] = useState(false);
  const [customDropdownVisible, setCustomDropdownVisible] = useState(false);
  const searchInputRef = useRef(null);
  const hasCustomDropdownContent = !!dropdownContent;
  const hasSearchDropdownContent = !!searchDropdownContent && searchValue.trim().length > 0;
  const activeCustomContent =
    activeControl === 'search' && hasSearchDropdownContent
      ? searchDropdownContent
      : dropdownContent;
  const shouldShowCustomContent =
    (activeControl === 'dropdown' && hasCustomDropdownContent) ||
    (activeControl === 'search' && hasSearchDropdownContent);

  useEffect(() => {
    if (!shouldShowCustomContent) {
      setCustomDropdownReady(false);
      setCustomDropdownVisible(false);
      return undefined;
    }

    setCustomDropdownReady(false);
    setCustomDropdownVisible(false);

    const readyTimer = window.setTimeout(() => {
      setCustomDropdownReady(true);
      window.requestAnimationFrame(() => setCustomDropdownVisible(true));
    }, CUSTOM_DROPDOWN_CONTENT_DELAY_MS);

    return () => window.clearTimeout(readyTimer);
  }, [shouldShowCustomContent]);

  useEffect(() => {
    if (activeControl !== 'search') return undefined;

    const focusTimer = window.setTimeout(() => searchInputRef.current?.focus(), 190);
    return () => window.clearTimeout(focusTimer);
  }, [activeControl]);

  const handleSearchChange = (event) => {
    onSearchValueChange(event.target.value, event);
  };

  const handleSearch = () => {
    onSearch(searchValue);
  };

  const renderDropdownContent = (content) => {
    if (!isValidElement(content)) return content;
    return cloneElement(content, {
      triggerClassName:
        'w-100 h-100 d-flex align-items-center rounded-0 border-0 bg-transparent no-caret shadow-none px-2',
      triggerStyle: {
        minHeight: 38,
        fontSize: 14,
        ...content.props.triggerStyle,
      },
    });
  };

  const segmentTransition =
    'flex-grow 240ms ease, flex-basis 240ms ease, max-width 240ms ease, min-width 240ms ease, gap 180ms ease, border-radius 180ms ease, box-shadow 180ms ease';
  const searchIsActive = activeControl === 'search';
  const dropdownIsActive = activeControl === 'dropdown';
  // react-select has no equivalent of the native leading <option value="">, and the control is
  // not clearable, so the default state needs a real option or it becomes unreachable once the
  // user picks something.
  const selectOptions = [
    { value: '', label: defaultOptionLabel },
    ...options.map((option) => ({
      value: option.value ?? '',
      label: option.label || option.value,
    })),
  ];

  return (
    <div
      className={`base-search-dropdown d-flex gap-1 flex-shrink-0 ${className}`}
      style={{ position: 'relative', ...style }}
    >
      <div
        className={`base-search-dropdown-segment d-flex align-items-center bg-primary rounded-3 px-2 py-0 overflow-hidden ${
          searchIsActive ? 'is-active' : ''
        }`}
        onClick={() => setActiveControl('search')}
        style={{
          flexGrow: searchIsActive ? 1 : 0,
          flexShrink: 1,
          flexBasis: searchIsActive ? 'calc(100% - 42px)' : 38,
          minWidth: searchIsActive ? 0 : 38,
          maxWidth: searchIsActive ? '100%' : 38,
          cursor: 'text',
          transition: segmentTransition,
          justifyContent: searchIsActive ? 'flex-start' : 'center',
          minHeight: 38,
          gap: searchIsActive ? 8 : 0,
        }}
      >
        <div
          className="d-flex align-items-center justify-content-center flex-shrink-0"
          style={{ width: 16, height: 16 }}
        >
          <BsSearch className="text-white" size={16} />
        </div>
        {searchIsActive && (
          <Form.Control
            ref={searchInputRef}
            type="text"
            placeholder={searchPlaceholder}
            aria-label={searchAriaLabel}
            value={searchValue}
            size="sm"
            className="base-search-dropdown-input bg-transparent border-0 text-white shadow-none px-0"
            style={{
              minHeight: 28,
              fontSize: 14,
              minWidth: 0,
              animation: 'baseSearchDropdownInputFade 160ms ease 80ms both',
            }}
            onFocus={() => setActiveControl('search')}
            onChange={handleSearchChange}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleSearch();
              }
            }}
          />
        )}
        {searchIsActive && searchValue ? (
          <Button
            type="button"
            variant="link"
            className="text-white p-0 ms-1 d-inline-flex align-items-center justify-content-center flex-shrink-0 text-decoration-none"
            style={{ width: 20, height: 20, lineHeight: 1 }}
            aria-label="Clear search"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onSearchValueChange('', event);
              searchInputRef.current?.focus();
            }}
          >
            <FaXmark size={14} />
          </Button>
        ) : null}
      </div>
      <div
        className={`base-search-dropdown-segment d-flex align-items-center bg-primary rounded-3 px-2 py-0 ${
          dropdownIsActive ? 'is-active' : ''
        } ${dropdownIsActive && hasCustomDropdownContent ? 'overflow-visible' : 'overflow-hidden'}`}
        onClick={() => setActiveControl('dropdown')}
        style={{
          flexGrow: dropdownIsActive ? 1 : 0,
          flexShrink: 1,
          flexBasis: dropdownIsActive ? 'calc(100% - 42px)' : 38,
          minWidth: dropdownIsActive ? 0 : 38,
          maxWidth: dropdownIsActive ? '100%' : 38,
          cursor: 'pointer',
          transition: segmentTransition,
          justifyContent: dropdownIsActive ? 'flex-start' : 'center',
          minHeight: 38,
          gap: dropdownIsActive ? 8 : 0,
          paddingLeft: dropdownIsActive && hasCustomDropdownContent ? 0 : undefined,
          paddingRight: dropdownIsActive && hasCustomDropdownContent ? 0 : undefined,
        }}
      >
        {dropdownIsActive && hasCustomDropdownContent ? (
          <div
            className={`flex-grow-1 min-w-0 w-100 ${dropdownContainerClassName}`}
            style={{
              opacity: customDropdownReady && customDropdownVisible ? 1 : 0,
              transform:
                customDropdownReady && customDropdownVisible ? 'translateY(0)' : 'translateY(-4px)',
              transition: `opacity ${CUSTOM_DROPDOWN_ANIMATION_MS}ms ease, transform ${CUSTOM_DROPDOWN_ANIMATION_MS}ms ease`,
              pointerEvents: customDropdownReady ? 'auto' : 'none',
              zIndex: 'var(--z-dropdown)',
            }}
            onClick={(event) => event.stopPropagation()}
          >
            {renderDropdownContent(activeCustomContent)}
          </div>
        ) : (
          <>
            <div
              className="d-flex align-items-center justify-content-center flex-shrink-0"
              style={{ width: 18, height: 18 }}
            >
              <BsChevronDown className="text-white" size={18} />
            </div>
            {dropdownIsActive ? (
              <BaseSelect
                wrapperClassName="flex-grow-1 min-w-0"
                isSearchable
                styles={DROPDOWN_SELECT_STYLES}
                selectProps={{
                  'aria-label': selectAriaLabel,
                  options: selectOptions,
                  value: findSelectOption(selectOptions, selectedValue),
                  placeholder: defaultOptionLabel,
                  autoFocus: true,
                  onFocus: () => setActiveControl('dropdown'),
                  onChange: (option) => onSelect(option?.value ?? ''),
                }}
              />
            ) : null}
          </>
        )}
      </div>
      {activeControl === 'search' && hasSearchDropdownContent && (
        <div
          className={`position-absolute start-0 end-0 ${dropdownContainerClassName}`}
          style={{
            top: 'calc(100% + 0.25rem)',
            zIndex: 'var(--z-dropdown)',
            overflow: 'hidden',
            backgroundColor: 'var(--bs-body-bg)',
            borderRadius: '0.375rem',
            boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.35)',
            opacity: customDropdownReady && customDropdownVisible ? 1 : 0,
            transform:
              customDropdownReady && customDropdownVisible ? 'translateY(0)' : 'translateY(-4px)',
            transition: `opacity ${CUSTOM_DROPDOWN_ANIMATION_MS}ms ease, transform ${CUSTOM_DROPDOWN_ANIMATION_MS}ms ease`,
          }}
          onClick={(event) => event.stopPropagation()}
        >
          {activeCustomContent}
        </div>
      )}
    </div>
  );
}
